/* eslint-disable no-alert -- Temporary: Alerts used for user feedback */
import { useCallback } from 'react';

import { useAppDispatch } from '@/store';
import { downloadAndDecryptFile } from '@/store/thunks/poc/helia/downloadAndDecryptFileThunk';
import type { FileMetadata } from '@/types/poc/helia';
import { createLogger, type Logger } from '@/utils/logger';

const log: Logger = createLogger({ namespace: 'six3ro:hooks:file-download' });

/**
 * Hook for handling file downloads from a Six
 * Manages file download, decryption, and browser download trigger
 *
 * @param sixId - ID of the Six to download files from
 * @returns {Object} Hook handlers
 * @returns {Function} return.handleDownload - Download and decrypt a file
 *
 * @example
 * const { handleDownload } = useSixFileDownload(sixId);
 *
 * return (
 *   <Button onClick={() => handleDownload(fileMetadata)}>
 *     Download
 *   </Button>
 * );
 */
export function useSixFileDownload(sixId: string) {
  const dispatch = useAppDispatch();

  const handleDownload = useCallback((fileMetadata: FileMetadata) => {
    log.info('Downloading file with CID:', fileMetadata.cid);

    void (async() => {
      try {
        const result = await dispatch(downloadAndDecryptFile({sixId, fileMetadata}));

        if (downloadAndDecryptFile.fulfilled.match(result)) {
          // Create a download link and trigger it
          const { decryptedFile, originalFileName } = result.payload;
          const url = URL.createObjectURL(decryptedFile);
          const link = document.createElement('a');
          link.href = url;
          link.download = originalFileName;
          document.body.appendChild(link);
          link.click();
          link.remove();
          URL.revokeObjectURL(url);
        } else {
          const errorMessage = typeof result.payload === 'string' ? result.payload : 'Unknown error';
          alert(`Download failed: ${errorMessage}`);
        }
      } catch (error) {
        alert(`Download error: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    })();
  }, [dispatch, sixId]);

  return {
    handleDownload,
  };
}
